/**
 * HARD deletion of published builds from the Registry page (operator request
 * 2026-10-14; internal/compiler/plans/2026-10-14-hard-delete-builds.md).
 * Withdraw keeps a build installable when pinned; Delete removes it from
 * registry storage, registry metadata, the publish pipeline's records and the
 * edge caches, and tombstones its version number forever.
 *
 * The flow, driven by components/registry/DeleteBuildsDialog.tsx:
 *   1. the operator selects one or more builds (checkboxes in VersionBuilds);
 *   2. the dialog asks the broker for a DRY RUN (`planBody`), which lists
 *      every object, row, pointer and byte that would go;
 *   3. a refusal that needs a decision (the X.Y.Z pointer sits on a deleted
 *      build, a dist-tag points at one, it is the last build of a version) is
 *      turned into explicit buttons (`actionsFor`) — never an automatic
 *      choice, so no pointer is ever left dangling by default;
 *   4. the delete button stays disabled until the plan is accepted, a reason
 *      is given and the confirmation text is typed exactly (`canConfirm`);
 *   5. the request carries the integrity the operator saw (`deleteBody`), so
 *      a build that changed since the listing is refused.
 *
 * Pure helpers only (unit-tested by buildDeletion.test.ts).
 */

import { lastSeenLabel } from "./downloads.ts";

export interface PlanRefusal {
  code: string;
  message: string;
  version?: string;
  base?: string;
  tag?: string;
  suggestion?: string | null;
}

export interface PlanObject {
  package: string;
  version: string;
  kind: "build" | "pointer" | "platform";
  file: string;
  key: string;
  bytes: number | null;
  exists: boolean;
  sharedKept: boolean;
}

export interface PlanBuild {
  version: string;
  base: string;
  compilerVersion: string;
  integrity: string;
  withdrawn: boolean;
  pointsHere: boolean;
  objects: PlanObject[];
  platformPackages: Array<{ name: string; version: string; present: boolean }>;
  downloads?: { total: number; lastSeen: number | null } | null;
}

export interface RemainingBuild {
  version: string;
  compilerVersion: string;
  withdrawn: boolean;
  hasPointer: boolean;
}

export interface DeletePlanView {
  ok: boolean;
  refusals: PlanRefusal[];
  builds: PlanBuild[];
  pointerChanges: Array<{
    base: string;
    from: string | null;
    to: string | null;
    wholeVersion: boolean;
  }>;
  tagChanges: Array<{ tag: string; from: string; to: string | null }>;
  remaining: Record<string, RemainingBuild[]>;
  tombstones: Array<{ package: string; version: string }>;
  totals: { objects: number; bytes: number; bytesUnknown: boolean };
  /** Downloads of the builds being deleted, summed (registry backend/
   *  downloads.ts); counting started with registry migration 0004. */
  installs: {
    tracked: boolean;
    total: number;
    d7: number;
    d30: number;
    bots: number;
    firstSeen: number | null;
    lastSeen: number | null;
  };
  lockfileImpact: string;
  confirm: string;
}

export interface PipelineCounts {
  publish_versions: { deleted: number; rewritten: number };
  publish_runs: number;
  publish_bench_results: number;
}

export interface PlanResponse {
  ok: boolean;
  plan: DeletePlanView;
  pipeline: PipelineCounts;
  /** The compile jobs' pure-jobs R2 objects that go with the builds. */
  artifacts?: {
    jobs: number;
    objects: number;
    bytes: number;
    active: string[];
    missing: string[];
  };
  confirm: string;
}

/** The decisions the operator made to resolve refusals. */
export interface Choices {
  wholeVersion: boolean;
  /** base X.Y.Z → build it points at afterwards; null = upstream's. */
  repointTo: Record<string, string | null>;
  /** dist-tag → version, null = drop. */
  distTags: Record<string, string | null>;
}

export const EMPTY_CHOICES: Choices = {
  wholeVersion: false,
  repointTo: {},
  distTags: {},
};

/** What must be typed: the exact version for one build, else the count and
 *  package. Mirrors the broker's and the registry's confirmPhrase (both
 *  enforce it again server-side). */
export function confirmPhrase(pkg: string, versions: string[]): string {
  const uniq = [...new Set(versions)];
  return uniq.length === 1
    ? uniq[0]!
    : `delete ${uniq.length} builds of ${pkg}`;
}

export function toggleSelected(selected: string[], version: string): string[] {
  return selected.includes(version)
    ? selected.filter((v) => v !== version)
    : [...selected, version];
}

/** Select every build of a list that is withdrawn (the usual cleanup). */
export function selectWithdrawn(
  builds: Array<{ version: string; withdrawn: unknown }>,
): string[] {
  return builds.filter((b) => b.withdrawn).map((b) => b.version);
}

export function planBody(pkg: string, versions: string[], c: Choices) {
  return {
    name: pkg,
    versions: [...new Set(versions)],
    wholeVersion: c.wholeVersion,
    repointTo: c.repointTo,
    distTags: c.distTags,
  };
}

/** The delete request: the plan's fields plus reason, the typed text and the
 *  integrity of each build as listed to the operator. */
export function deleteBody(
  pkg: string,
  versions: string[],
  c: Choices,
  reason: string,
  typed: string,
  shown: DeletePlanView,
) {
  return {
    ...planBody(pkg, versions, c),
    reason: reason.trim(),
    confirm: typed,
    expect: Object.fromEntries(
      shown.builds.map((b) => [b.version, b.integrity]),
    ),
  };
}

/** The delete button is enabled only for an accepted plan, a reason and the
 *  exact confirmation. */
export function canConfirm(
  pkg: string,
  versions: string[],
  resp: PlanResponse | null,
  reason: string,
  typed: string,
): boolean {
  return (
    resp !== null &&
    resp.plan.ok &&
    reason.trim() !== "" &&
    typed === confirmPhrase(pkg, versions)
  );
}

export interface Action {
  /** Stable key for React. */
  key: string;
  label: string;
  apply: (c: Choices) => Choices;
}

/** The explicit buttons a refusal needs. Nothing is chosen for the operator:
 *  "re-point X.Y.Z to the previous build" is offered, not applied. */
export function actionsFor(r: PlanRefusal, resp: PlanResponse): Action[] {
  switch (r.code) {
    case "EPOINTER": {
      const base = r.base!;
      const out: Action[] = [];
      for (const b of resp.plan.remaining[base] ?? []) {
        if (b.withdrawn || !b.hasPointer) continue;
        out.push({
          key: `repoint:${base}:${b.version}`,
          label:
            b.version === r.suggestion
              ? `Delete and re-point ${base} to ${b.version} (previous build)`
              : `Delete and re-point ${base} to ${b.version}`,
          apply: (c) => ({
            ...c,
            repointTo: { ...c.repointTo, [base]: b.version },
          }),
        });
      }
      // The suggested (newest live) build first.
      out.sort(
        (a, b) =>
          Number(b.label.includes("(previous build)")) -
          Number(a.label.includes("(previous build)")),
      );
      out.push({
        key: `repoint:${base}:upstream`,
        label: `Delete and serve upstream's ${base} (no Pure build)`,
        apply: (c) => ({ ...c, repointTo: { ...c.repointTo, [base]: null } }),
      });
      return out;
    }
    case "EDISTTAG": {
      const tag = r.tag!;
      const out: Action[] = [];
      for (const list of Object.values(resp.plan.remaining))
        for (const b of list)
          out.push({
            key: `tag:${tag}:${b.version}`,
            label: `Move dist-tag "${tag}" to ${b.version}`,
            apply: (c) => ({
              ...c,
              distTags: { ...c.distTags, [tag]: b.version },
            }),
          });
      out.push({
        key: `tag:${tag}:drop`,
        label: `Remove dist-tag "${tag}"`,
        apply: (c) => ({ ...c, distTags: { ...c.distTags, [tag]: null } }),
      });
      return out;
    }
    case "EONLYBUILD":
      return [
        {
          key: `whole:${r.base}`,
          label: `Delete the whole package version ${r.base} (every build of it)`,
          apply: (c) => ({ ...c, wholeVersion: true }),
        },
      ];
    default:
      return [];
  }
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KiB", "MiB", "GiB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

/** The "what will be removed" listing, one line per store. */
export function describePlan(resp: PlanResponse): string[] {
  const { plan, pipeline } = resp;
  const lines: string[] = [];
  const bytes = plan.totals.bytesUnknown
    ? `${formatBytes(plan.totals.bytes)}+ (some sizes unknown)`
    : formatBytes(plan.totals.bytes);
  lines.push(
    `Registry storage: ${plan.totals.objects} object${plan.totals.objects === 1 ? "" : "s"}, ${bytes}`,
  );
  for (const b of plan.builds) {
    const kept = b.objects.filter((o) => o.sharedKept).length;
    lines.push(
      `  ${b.version}: ${b.objects
        .map((o) => `${o.kind}${o.exists ? "" : " (already missing)"}`)
        .join(
          ", ",
        )}${b.platformPackages.length ? ` — platform packages: ${b.platformPackages.map((p) => p.name).join(", ")}` : ""}${kept ? ` — ${kept} shared object(s) kept` : ""}`,
    );
  }
  for (const c of plan.pointerChanges)
    lines.push(
      c.wholeVersion
        ? `Pointer: ${c.base} → upstream's original (no build of it remains; automatic rebuilds stay blocked until an operator Rebuild)`
        : `Pointer: ${c.base} → ${c.to ?? "upstream's original"} (was ${c.from})`,
    );
  for (const t of plan.tagChanges)
    lines.push(
      t.to === null
        ? `dist-tag "${t.tag}" removed (was ${t.from})`
        : `dist-tag "${t.tag}" → ${t.to} (was ${t.from})`,
    );
  lines.push(
    `Registry metadata: version entries, integrity and index rows of ${plan.builds.length} build${plan.builds.length === 1 ? "" : "s"} removed`,
  );
  const pv = pipeline.publish_versions;
  lines.push(
    `Publish pipeline: ${pv.deleted} version row${pv.deleted === 1 ? "" : "s"} deleted${pv.rewritten ? `, ${pv.rewritten} re-pointed at a remaining build` : ""}, ${pipeline.publish_runs} run event${pipeline.publish_runs === 1 ? "" : "s"}, ${pipeline.publish_bench_results} benchmark result${pipeline.publish_bench_results === 1 ? "" : "s"} (the /-/stats page stops listing them)`,
  );
  const dl = plan.installs;
  lines.push(
    dl.total === 0
      ? "Downloads: none recorded"
      : `Downloads: ${dl.total} (${dl.d7} in the last 7 days, ${dl.d30} in the last 30), last seen ${lastSeenLabel(dl.lastSeen)}${dl.bots ? `; ${dl.bots} bot/scanner requests not counted` : ""}`,
  );
  if (resp.artifacts)
    lines.push(
      `Compile artifacts: ${resp.artifacts.objects} object${resp.artifacts.objects === 1 ? "" : "s"}, ${formatBytes(resp.artifacts.bytes)} of ${resp.artifacts.jobs} compile job${resp.artifacts.jobs === 1 ? "" : "s"} (addon sets, source maps, logs, failure bundles) deleted; the job rows and their telemetry stay and read "artifacts deleted"`,
    );
  lines.push(
    `Tombstoned forever: ${plan.tombstones.map((t) => t.version).join(", ")} (never published again)`,
  );
  lines.push("Caches: the pure.dev edge cache is purged afterwards");
  return lines;
}

/** The consequence for existing installs with the registry's real download
 *  numbers: "N downloads, last seen …; lockfiles pinned to this build will
 *  404". Downloads counted before registry migration 0004 do not exist, and
 *  installs served from a client's own cache never reached the registry. */
export function lockfileWarning(resp: PlanResponse, now = Date.now()): string {
  const i = resp.plan.installs;
  const seen =
    i.total === 0
      ? "no downloads recorded (counting started 2026-10-14; cached installs never reach the registry)."
      : `${i.total} download${i.total === 1 ? "" : "s"} (${i.d7} in the last 7 days, ${i.d30} in the last 30), last seen ${lastSeenLabel(i.lastSeen, now)}.`;
  return `Lockfiles that recorded a deleted build will fail to install it (HTTP 404) — ${seen} Builds that stay are unaffected. This cannot be undone.`;
}

export type DeletionState =
  | "deleting"
  | "registry_done"
  | "artifacts_done"
  | "pipeline_done"
  | "done"
  | "aborted";

const STATE_LABELS: Record<DeletionState, string> = {
  deleting: "removing from the registry",
  registry_done: "deleting compile artifacts",
  artifacts_done: "removing pipeline records",
  pipeline_done: "purging caches",
  done: "deleted",
  aborted: "refused (nothing was changed)",
};

/** One line for a deletion's state; an `error` means it is stuck and being
 *  retried (every minute) from where it stopped. */
export function deletionStatusLine(
  state: DeletionState,
  error: string | null,
): string {
  const base = STATE_LABELS[state];
  if (state === "done" || state === "aborted")
    return error ? `${base}: ${error}` : base;
  return error ? `${base} — retrying automatically (${error})` : `${base}…`;
}

export function isFinal(state: DeletionState): boolean {
  return state === "done" || state === "aborted";
}
